<!--
  Flexi › Safety rounds › one round. Details → Checkpoints → Complete.
  Grades save at once and comments after a pause; findings (2–4) are followed
  up as deviations from the panel under them. Read-only once completed, and
  for anyone who is not an author.
-->
<section class="fvr-card fvr-round" [attr.aria-busy]="loading || completing">
  <header class="fvr-round__head">
    <a class="fvr-back" href="javascript:void(0)" (click)="backToList()">
      <i class="pi pi-angle-left" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.TAB' | translate }}
    </a>

    @if (round; as r) {
      <div class="fvr-round__title-row">
        <h2 class="fvr-title">{{ r.Title }}</h2>
        @if (r.Status === Status.Completed) {
          <span class="fvr-pill fvr-pill--done">{{ 'FLEXI_SAFETY_ROUNDS.STATUS.COMPLETED' | translate }}</span>
        } @else {
          <span class="fvr-pill fvr-pill--progress">{{ 'FLEXI_SAFETY_ROUNDS.STATUS.IN_PROGRESS' | translate }}</span>
        }
      </div>
      <p class="fvr-subtitle">
        {{ r.TemplateName || ('FLEXI_SAFETY_ROUNDS.NO_TEMPLATE' | translate) }}
        @if (r.ResponsibleUserName) {
          · {{ 'FLEXI_SAFETY_ROUNDS.RESPONSIBLE_NAMED' | translate: { name: r.ResponsibleUserName } }}
        }
        @if (r.Location) {
          · {{ 'FLEXI_SAFETY_ROUNDS.LOCATION_NAMED' | translate: { location: r.Location } }}
        }
        · {{ 'FLEXI_SAFETY_ROUNDS.STARTED_ON' | translate: { date: (r.RoundDate | date: 'dd-MM-yyyy') } }}
      </p>
    }
  </header>

  @if (loading && !round) {
    <flexi-safety-round-skeleton layout="form" />
  }

  @if (loadFailed) {
    <p class="fas-error" role="alert">{{ 'FLEXI_SAFETY_ROUNDS.ROUND_LOAD_FAILED' | translate }}</p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_SAFETY_ROUNDS.ASSIGN.RETRY' | translate }}</button>
  }

  @if (completing || downloading || bulkBusy || savingDetails || refreshing) {
    <p class="fvr-action-status" role="status"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
      {{ (completing ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.COMPLETING' : downloading ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.REPORT' : savingDetails ? 'FLEXI_SAFETY_ROUNDS.SAVING' : 'FLEXI_SAFETY_ROUNDS.FEEDBACK.UPDATING') | translate }}
    </p>
  }
  <div class="fvr-operation-content" [attr.inert]="completing ? '' : null">
  @if (round; as r) {
    <!-- ═══════════ Steps ═══════════ -->
    <div class="fvr-steps" role="tablist" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.STEPS_LABEL' | translate">
      @for (s of steps; track s; let i = $index) {
        <button
          type="button"
          role="tab"
          class="fvr-step"
          [class.fvr-step--active]="step === s"
          [attr.aria-selected]="step === s"
          (click)="selectStep(s)"
        >
          <span class="fvr-step__num">{{ i + 1 }}</span>
          {{ 'FLEXI_SAFETY_ROUNDS.STEP.' + s | translate }}
        </button>
      }
    </div>

    <!-- ═══════════ Step 1: details ═══════════ -->
    @if (step === 'details') {
      @if (detailsSaveFailed) {
        <p class="fas-error" role="alert">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.SAVE_FAILED' | translate }}</p>
      }
      <form class="fvr-details" [attr.inert]="savingDetails ? '' : null" (ngSubmit)="saveDetails()" novalidate>
        <div class="fvr-details__grid">
          <div class="fvr-field">
            <label for="fvr-title">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.TITLE' | translate }} <span class="fvr-required">*</span></label>
            <input
              pInputText
              id="fvr-title"
              name="title"
              type="text"
              maxlength="200"
              [(ngModel)]="title"
              [disabled]="readOnly"
              [attr.aria-invalid]="detailsSubmitted && !title.trim()"
            />
            @if (detailsSubmitted && !title.trim()) {
              <span class="fvr-field-error">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.TITLE_REQUIRED' | translate }}</span>
            }
          </div>
          <div class="fvr-field">
            <label for="fvr-date">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.DATE' | translate }}</label>
            <input pInputText id="fvr-date" type="text" [value]="r.RoundDate | date: 'dd-MM-yyyy'" readonly />
          </div>
          <div class="fvr-field">
            <label for="fvr-responsible">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.RESPONSIBLE' | translate }}</label>
            <p-select
              inputId="fvr-responsible"
              name="responsible"
              [options]="users"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="responsibleUserId"
              [filter]="true"
              [showClear]="true"
              [disabled]="readOnly"
              appendTo="body"
              [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate"
              styleClass="fvr-select"
            ></p-select>
          </div>
          <div class="fvr-field">
            <label for="fvr-location">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.LOCATION' | translate }}</label>
            <input pInputText id="fvr-location" name="location" type="text" maxlength="200" [(ngModel)]="location" [disabled]="readOnly" />
          </div>
          <div class="fvr-field">
            <label for="fvr-department">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.DEPARTMENT' | translate }}</label>
            <p-select
              inputId="fvr-department"
              name="department"
              [options]="departments"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="departmentId"
              [filter]="true"
              [showClear]="true"
              [disabled]="readOnly"
              appendTo="body"
              [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate"
              styleClass="fvr-select"
            ></p-select>
          </div>
        </div>
        <div class="fvr-field">
          <label for="fvr-participants">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.PARTICIPANTS' | translate }}</label>
          <textarea
            pTextarea
            id="fvr-participants"
            name="participants"
            rows="3"
            maxlength="2000"
            [(ngModel)]="participants"
            [disabled]="readOnly"
          ></textarea>
          <span class="fvr-hint">{{ 'FLEXI_SAFETY_ROUNDS.DETAILS.PARTICIPANTS_HINT' | translate }}</span>
        </div>
        <div class="fvr-details__actions">
          <button type="button" class="fas-btn fas-btn--ghost" (click)="backToList()">{{ 'FLEXI_SAFETY_ROUNDS.BACK' | translate }}</button>
          <button type="submit" class="fas-btn fas-btn--primary" [disabled]="savingDetails">
            @if (savingDetails) {
              <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
            }
            {{ (savingDetails ? 'FLEXI_SAFETY_ROUNDS.SAVING' : readOnly ? 'FLEXI_SAFETY_ROUNDS.NEXT' : 'FLEXI_SAFETY_ROUNDS.DETAILS.SAVE_NEXT') | translate }}
          </button>
        </div>
      </form>
    }

    <!-- ═══════════ Step 2: checkpoints ═══════════ -->
    @if (step === 'checkpoints') {
      <div class="fvr-points">
        <div class="fvr-points__status">
          <div class="fvr-progress fvr-points__progress">
            <div
              class="fvr-progress__track"
              role="progressbar"
              [attr.aria-valuemin]="0"
              [attr.aria-valuemax]="progress.total"
              [attr.aria-valuenow]="progress.graded"
              [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.PROGRESS_LABEL' | translate"
            >
              <div class="fvr-progress__fill" [style.width.%]="progress.percent"></div>
            </div>
            <span class="fvr-progress__label">
              {{ 'FLEXI_SAFETY_ROUNDS.ANSWERED_OF' | translate: { graded: progress.graded, total: progress.total } }}
            </span>
          </div>
          <div class="fvr-chips">
            @if (distribution.danger) {
              <span class="fvr-grade fvr-grade--4">{{ 'FLEXI_SAFETY_ROUNDS.FINDING_CHIP' | translate: { grade: 4, count: distribution.danger } }}</span>
            }
            @if (distribution.legalBreach) {
              <span class="fvr-grade fvr-grade--3">{{ 'FLEXI_SAFETY_ROUNDS.FINDING_CHIP' | translate: { grade: 3, count: distribution.legalBreach } }}</span>
            }
            @if (distribution.shouldImprove) {
              <span class="fvr-grade fvr-grade--2">{{ 'FLEXI_SAFETY_ROUNDS.FINDING_CHIP' | translate: { grade: 2, count: distribution.shouldImprove } }}</span>
            }
          </div>
        </div>

        <div class="fvr-points__toolbar">
          <label class="fvr-search">
            <span class="fas-sr-only">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.SEARCH' | translate }}</span>
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              pInputText
              type="search"
              maxlength="200"
              [(ngModel)]="query"
              (ngModelChange)="updateVisible()"
              [placeholder]="'FLEXI_SAFETY_ROUNDS.CHECK.SEARCH' | translate"
            />
          </label>
          <div class="fvr-filter" role="group" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.CHECK.FILTER' | translate">
            @for (f of ['all', 'unanswered', 'findings']; track f) {
              <button
                type="button"
                class="fvr-filter__chip"
                [class.fvr-filter__chip--active]="filter === f"
                [attr.aria-pressed]="filter === f"
                (click)="pickFilter($any(f))"
              >
                {{ 'FLEXI_SAFETY_ROUNDS.CHECK.FILTER_' + f.toUpperCase() | translate }}
                <span class="fvr-filter__count">{{ filterCount($any(f)) }}</span>
              </button>
            }
          </div>
        </div>

        <div class="fvr-points__body">
          @if (sections.length) {
            <nav class="fvr-outline" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.CHECK.OUTLINE' | translate">
              <span class="fvr-eyebrow">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.OUTLINE' | translate }}</span>
              @for (section of sections; track section.name; let i = $index) {
                <button
                  type="button"
                  class="fvr-outline__item"
                  [class.fvr-outline__item--current]="!searching && i === sectionIndex"
                  [attr.aria-current]="!searching && i === sectionIndex ? 'true' : null"
                  (click)="pickSection(i)"
                >
                  <span class="fvr-outline__name">{{ section.name }}</span>
                  <span class="fvr-outline__count">
                    @if (section.findings) {
                      <span class="fvr-outline__dot" [attr.title]="'FLEXI_SAFETY_ROUNDS.CHECK.HAS_FINDINGS' | translate"></span>
                    }
                    {{ section.graded }}/{{ section.items.length }}
                  </span>
                </button>
              }
            </nav>
          }

          <div class="fvr-points__main">
            @if (sections.length > 1) {
              <div class="fvr-field fvr-outline-select">
                <label for="fvr-section-select">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.SECTION' | translate }}</label>
                <p-select
                  inputId="fvr-section-select"
                  [options]="sectionOptions"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="sectionIndex"
                  (ngModelChange)="pickSection($event)"
                  appendTo="body"
                  styleClass="fvr-select"
                ></p-select>
              </div>
            }

            @if (searching) {
              <div class="fvr-points__section-head">
                <h3 class="fvr-h3">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.SEARCH_RESULTS' | translate: { count: visibleItems.length } }}</h3>
              </div>
            } @else if (currentSection; as section) {
              <div class="fvr-points__section-head">
                <h3 class="fvr-h3">{{ section.name }}</h3>
                <span class="fas-muted">
                  {{ 'FLEXI_SAFETY_ROUNDS.CHECK.SECTION_INFO' | translate: { index: sectionIndex + 1, sections: sections.length, graded: section.graded, total: section.items.length } }}
                </span>
              </div>
            }

            @for (item of visibleItems; track item.Id) {
              <article
                class="fvr-item"
                [class.fvr-item--finding]="isFinding(item.Grade)"
                [attr.aria-labelledby]="'fvr-item-' + item.Id"
              >
                <div class="fvr-item__head">
                  <span class="fvr-item__num">{{ itemNumbers.get(item.Id) }}</span>
                  <div class="fvr-item__text">
                    @if (searching) {
                      <span class="fvr-eyebrow">{{ item.SectionName }}</span>
                    }
                    <h4 class="fvr-item__title" [id]="'fvr-item-' + item.Id">
                      {{ item.Title }}
                      @if (item.IsAdHoc) {
                        <span class="fas-badge fas-badge--inactive">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.AD_HOC' | translate }}</span>
                      }
                    </h4>
                    @if (item.Guidance) {
                      <p class="fvr-item__guide">{{ item.Guidance }}</p>
                    }
                  </div>
                </div>

                <!-- Disabled buttons swallow hover, so the lock explanation sits on the group. -->
                <div
                  class="fvr-grades"
                  role="group"
                  [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.CHECK.GRADE_GROUP' | translate: { title: item.Title }"
                  [pTooltip]="item.Deviation && !readOnly ? ('FLEXI_SAFETY_ROUNDS.CHECK.GRADE_LOCKED' | translate) : ''"
                  tooltipPosition="top"
                >
                  @for (option of gradeOptions; track option.grade) {
                    <button
                      type="button"
                      class="fvr-grade-btn"
                      [ngClass]="'fvr-grade-btn--' + option.grade"
                      [class.fvr-grade-btn--active]="item.Grade === option.grade"
                      [attr.aria-pressed]="item.Grade === option.grade"
                      [disabled]="gradeLocked(item)"
                      (click)="setGrade(item, option.grade)"
                    >
                      <span class="fvr-grade-btn__num">{{ option.short }}</span>
                      <span class="fvr-grade-btn__label">{{ option.labelKey | translate }}</span>
                    </button>
                  }
                </div>

                <div class="fvr-item__tools">
                  <button
                    type="button"
                    class="fvr-tool"
                    [attr.aria-expanded]="expanded.has(item.Id) || isFinding(item.Grade)"
                    [disabled]="isFinding(item.Grade)"
                    (click)="toggleExpanded(item)"
                  >
                    <i class="pi pi-comment" aria-hidden="true"></i>
                    {{ (item.Comment ? 'FLEXI_SAFETY_ROUNDS.CHECK.COMMENT_FILLED' : 'FLEXI_SAFETY_ROUNDS.CHECK.COMMENT') | translate }}
                  </button>
                  <button
                    type="button"
                    class="fvr-tool"
                    [attr.aria-expanded]="expanded.has(item.Id) || isFinding(item.Grade)"
                    [disabled]="isFinding(item.Grade)"
                    (click)="toggleExpanded(item)"
                  >
                    <i class="pi pi-camera" aria-hidden="true"></i>
                    {{ 'FLEXI_SAFETY_ROUNDS.CHECK.PHOTOS' | translate: { count: item.Photos.length } }}
                  </button>
                  @if (item.StandardTexts.length && !readOnly) {
                    <button type="button" class="fvr-tool" aria-haspopup="menu" (click)="openStandardTexts($event, item)">
                      <i class="pi pi-list" aria-hidden="true"></i>
                      {{ 'FLEXI_SAFETY_ROUNDS.CHECK.STANDARD_TEXTS' | translate: { count: item.StandardTexts.length } }}
                    </button>
                  }
                  @if (item.RegulatoryReferences.length) {
                    <button
                      type="button"
                      class="fvr-tool"
                      [attr.aria-expanded]="refsOpen.has(item.Id)"
                      (click)="toggleRefs(item)"
                    >
                      <i class="pi pi-book" aria-hidden="true"></i>
                      {{ 'FLEXI_SAFETY_ROUNDS.CHECK.REGULATIONS' | translate: { count: item.RegulatoryReferences.length } }}
                    </button>
                  }
                </div>

                @if (refsOpen.has(item.Id)) {
                  <ul class="fvr-refs">
                    @for (ref of item.RegulatoryReferences; track $index) {
                      <li>
                        @if (ref.Url) {
                          <a
                            class="fvr-ref"
                            [href]="ref.Url"
                            target="_blank"
                            rel="noopener noreferrer"
                            (click)="openRegulation($event, ref.Url)"
                          >
                            {{ ref.Title }} <i class="pi pi-external-link" aria-hidden="true"></i>
                          </a>
                        } @else {
                          <span class="fvr-ref fvr-ref--plain">{{ ref.Title }}</span>
                        }
                      </li>
                    }
                  </ul>
                }

                @if (expanded.has(item.Id) || isFinding(item.Grade)) {
                  <div class="fvr-field fvr-item__comment">
                    @if (isFinding(item.Grade) && !readOnly) {
                      <span class="fvr-hint">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.FINDING_HINT' | translate }}</span>
                    }
                    <label [for]="'fvr-comment-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.COMMENT' | translate }}</label>
                    <textarea
                      pTextarea
                      rows="3"
                      maxlength="4000"
                      [id]="'fvr-comment-' + item.Id"
                      [(ngModel)]="item.Comment"
                      (ngModelChange)="commentChanged(item)"
                      [readonly]="readOnly"
                      [placeholder]="'FLEXI_SAFETY_ROUNDS.CHECK.COMMENT_PLACEHOLDER' | translate"
                    ></textarea>
                  </div>

                  <!-- Photos: shown to everyone, added and removed by authors only. -->
                  <div class="fvr-photos">
                    <span class="fvr-label">{{ 'FLEXI_SAFETY_ROUNDS.PHOTOS.TITLE' | translate }}</span>
                    <div class="fvr-photos__strip">
                      @for (photo of item.Photos; track photo.Id) {
                        <div class="fvr-photo">
                          <button
                            type="button"
                            class="fvr-photo__open"
                            [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.PHOTOS.OPEN' | translate: { name: photo.FileName }"
                            (click)="openPreview(photo)"
                          >
                            <img [src]="photo.Url" [alt]="photo.FileName" loading="lazy" (error)="photoLinkFailed()" />
                          </button>
                          @if (!readOnly) {
                            <button
                              type="button"
                              class="fvr-photo__remove"
                              [disabled]="removingPhotoIds.has(photo.Id)"
                              [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.PHOTOS.REMOVE' | translate: { name: photo.FileName }"
                              (click)="confirmRemovePhoto(item, photo)"
                            >
                              <i class="pi" [ngClass]="removingPhotoIds.has(photo.Id) ? 'pi-spin pi-spinner' : 'pi-times'" aria-hidden="true"></i>
                            </button>
                          }
                        </div>
                      }
                      @if (!readOnly) {
                        @if (uploading.has(item.Id)) {
                          <div class="fvr-photo fvr-photo--uploading" role="status">
                            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                            <span>{{ uploading.get(item.Id) }} %</span>
                          </div>
                        } @else if (!photoLimitReached(item)) {
                          <label class="fvr-photo fvr-photo--add" [for]="'fvr-photo-input-' + item.Id">
                            <i class="pi pi-camera" aria-hidden="true"></i>
                            <span>{{ 'FLEXI_SAFETY_ROUNDS.PHOTOS.ADD' | translate }}</span>
                          </label>
                          <input
                            class="fas-sr-only"
                            type="file"
                            [id]="'fvr-photo-input-' + item.Id"
                            [accept]="photoAccept"
                            capture="environment"
                            [disabled]="!canAddPhoto(item)"
                            (change)="photoPicked($event, item)"
                          />
                        }
                      }
                      @if (readOnly && !item.Photos.length) {
                        <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.PHOTOS.NONE' | translate }}</span>
                      }
                    </div>
                    @if (!readOnly) {
                      <span class="fvr-hint">{{ 'FLEXI_SAFETY_ROUNDS.PHOTOS.LIMITS' | translate: { max: photoMaxCount } }}</span>
                    }
                    @if (photoErrors.get(item.Id); as errorKey) {
                      <span class="fvr-field-error" role="alert">{{ errorKey | translate: { max: photoMaxCount } }}</span>
                    }
                    @if (photoRequired(item) && !item.Photos.length) {
                      <span class="fvr-hint fvr-hint--warn">
                        <i class="pi pi-camera" aria-hidden="true"></i>
                        {{ 'FLEXI_SAFETY_ROUNDS.CHECK.PHOTO_REQUIRED' | translate: { grade: item.Grade } }}
                      </span>
                    }
                  </div>
                }

                @if (isFinding(item.Grade)) {
                  <div class="fvr-dev" [ngClass]="'fvr-dev--' + item.Grade">
                    @if (item.Deviation; as dev) {
                      <div class="fvr-dev__head">
                        <div>
                          <strong>{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.NUMBER' | translate: { number: dev.Number } }}</strong>
                          <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.FOLLOWED_UP' | translate }}</span>
                        </div>
                        <span class="fvr-importance" [ngClass]="'fvr-importance--' + dev.Importance">
                          {{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.IMPORTANCE_LABEL' | translate: { importance: (importanceKey(dev.Importance) | translate) } }}
                        </span>
                      </div>
                      <dl class="fvr-dev__facts">
                        <div>
                          <dt>{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.OWNER' | translate }}</dt>
                          <dd>{{ dev.ExecutionUserName || '—' }}</dd>
                        </div>
                        <div>
                          <dt>{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.DEADLINE' | translate }}</dt>
                          <dd>{{ dev.Deadline ? (dev.Deadline | date: 'dd-MM-yyyy') : '—' }}</dd>
                        </div>
                      </dl>
                      <a class="fvr-link" [routerLink]="deviationUrl(dev.Id)">
                        {{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.OPEN' | translate }} <i class="pi pi-arrow-right" aria-hidden="true"></i>
                      </a>
                    } @else if (readOnly) {
                      <p class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.NONE' | translate }}</p>
                    } @else {
                      @let draft = draftFor(item);
                      @if (draft.saving) { <p role="status">{{ 'FLEXI_SAFETY_ROUNDS.FEEDBACK.CREATING_DEVIATION' | translate }}</p> }
                      <div class="fvr-operation-content" [attr.inert]="draft.saving ? '' : null">
                      <div class="fvr-dev__head">
                        <div>
                          <strong>{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.NEW_TITLE' | translate }}</strong>
                          <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.NEW_NOTE' | translate }}</span>
                        </div>
                        <span class="fvr-importance" [ngClass]="'fvr-importance--' + (item.Grade - 1)">
                          {{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.IMPORTANCE_LABEL' | translate: { importance: (expectedImportanceKey(item) | translate) } }}
                        </span>
                      </div>
                      <div class="fvr-dev__grid">
                        <div class="fvr-field">
                          <label [for]="'fvr-owner-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.OWNER' | translate }} <span class="fvr-required">*</span></label>
                          <p-select
                            [inputId]="'fvr-owner-' + item.Id"
                            [options]="users"
                            optionLabel="label"
                            optionValue="value"
                            [(ngModel)]="draft.executionUserId"
                            [filter]="true"
                            appendTo="body"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate"
                            styleClass="fvr-select"
                          ></p-select>
                        </div>
                        <div class="fvr-field">
                          <label [for]="'fvr-department-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.DEPARTMENT' | translate }} <span class="fvr-required">*</span></label>
                          <p-multiSelect [inputId]="'fvr-department-' + item.Id" [options]="departments"
                            optionLabel="label" optionValue="value" [filter]="true" appendTo="body"
                            [ngModel]="findingDepartments.get(item.Id)" (ngModelChange)="findingDepartments.set(item.Id, $event)"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate" styleClass="fvr-select"></p-multiSelect>
                        </div>
                        <div class="fvr-field">
                          <label [for]="'fvr-followup-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.FOLLOWUP' | translate }} <span class="fvr-required">*</span></label>
                          <p-select [inputId]="'fvr-followup-' + item.Id" [options]="users"
                            optionLabel="label" optionValue="value" [filter]="true" appendTo="body"
                            [ngModel]="findingFollowupUsers.get(item.Id)" (ngModelChange)="findingFollowupUsers.set(item.Id, $event)"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate" styleClass="fvr-select"></p-select>
                        </div>
                        <div class="fvr-field">
                          <label [for]="'fvr-deadline-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.DEADLINE' | translate }} <span class="fvr-required">*</span></label>
                          <p-datepicker
                            [inputId]="'fvr-deadline-' + item.Id"
                            [(ngModel)]="draft.deadline"
                            (onSelect)="deadlinePicked(draft)"
                            dateFormat="dd-mm-yy"
                            [showIcon]="true"
                            appendTo="body"
                            styleClass="fvr-select"
                          ></p-datepicker>
                        </div>
                        <div class="fvr-field">
                          <label [for]="'fvr-topic-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.TOPIC' | translate }} <span class="fvr-required">*</span></label>
                          <p-select
                            [inputId]="'fvr-topic-' + item.Id"
                            [options]="topics"
                            optionLabel="label"
                            optionValue="value"
                            [(ngModel)]="draft.topicId"
                            [filter]="topics.length > 8"
                            appendTo="body"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate"
                            styleClass="fvr-select"
                          ></p-select>
                        </div>
                        <div class="fvr-field">
                          <label [for]="'fvr-case-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.CASE_TYPE' | translate }} <span class="fvr-required">*</span></label>
                          <p-select
                            [inputId]="'fvr-case-' + item.Id"
                            [options]="caseTypes"
                            optionLabel="label"
                            optionValue="value"
                            [(ngModel)]="draft.caseId"
                            [filter]="caseTypes.length > 8"
                            appendTo="body"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.SELECT' | translate"
                            styleClass="fvr-select"
                          ></p-select>
                        </div>
                      </div>
                      @if (item.Grade === Grade.DangerToLifeOrHealth) {
                        <div class="fvr-field">
                          <label [for]="'fvr-immediate-' + item.Id">{{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.IMMEDIATE_ACTION' | translate }} <span class="fvr-required">*</span></label>
                          <textarea
                            pTextarea
                            rows="2"
                            maxlength="2000"
                            [id]="'fvr-immediate-' + item.Id"
                            [(ngModel)]="draft.immediateAction"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.DEVIATION.IMMEDIATE_PLACEHOLDER' | translate"
                          ></textarea>
                        </div>
                      }
                      @if (missingRequiredPhoto(item)) {
                        <p class="fvr-hint fvr-hint--warn" role="alert">
                          <i class="pi pi-camera" aria-hidden="true"></i>
                          {{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.PHOTO_NEEDED' | translate }}
                        </p>
                      }
                      <div class="fvr-dev__foot">
                        <span class="fvr-hint">
                          {{ 'FLEXI_SAFETY_ROUNDS.DEVIATION.HINT' | translate: { grade: item.Grade, importance: (expectedImportanceKey(item) | translate), days: dueDaysFor(item) } }}
                        </span>
                        <button
                          type="button"
                          class="fas-btn fas-btn--primary"
                          [disabled]="!canCreateDeviation(item, draft)"
                          (click)="createDeviation(item)"
                        >
                          @if (draft.saving) {
                            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                          }
                          {{ (draft.saving ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.CREATING_DEVIATION' : 'FLEXI_SAFETY_ROUNDS.DEVIATION.CREATE') | translate }}
                        </button>
                      </div>
                      </div>
                    }
                  </div>
                }
              </article>
            } @empty {
              <div class="fvr-empty">
                <i class="pi pi-filter-slash" aria-hidden="true"></i>
                <p>
                  {{ (sections.length ? 'FLEXI_SAFETY_ROUNDS.CHECK.NO_MATCH' : 'FLEXI_SAFETY_ROUNDS.CHECK.NO_ITEMS') | translate }}
                </p>
              </div>
            }

            @if (!readOnly && !searching) {
              @if (addMode === 'none') {
                <div class="fvr-points__add">
                  @if (currentSection) {
                    <button type="button" class="fas-btn fas-btn--text" (click)="openAdd('item')">
                      <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.CHECK.ADD_ITEM' | translate }}
                    </button>
                  }
                  <button type="button" class="fas-btn fas-btn--text" (click)="openAdd('area')">
                    <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.CHECK.ADD_AREA' | translate }}
                  </button>
                </div>
              } @else {
                <form class="fvr-add" (ngSubmit)="addItem()">
                  @if (addMode === 'area') {
                    <div class="fvr-field">
                      <label for="fvr-new-area">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.NEW_AREA_NAME' | translate }}</label>
                      <input pInputText id="fvr-new-area" name="area" type="text" maxlength="200" [(ngModel)]="newSectionName" />
                    </div>
                  }
                  <div class="fvr-field">
                    <label for="fvr-new-item">{{ 'FLEXI_SAFETY_ROUNDS.CHECK.NEW_ITEM_TITLE' | translate }}</label>
                    <input pInputText id="fvr-new-item" name="title" type="text" maxlength="300" [(ngModel)]="newItemTitle" />
                  </div>
                  <div class="fvr-add__actions">
                    <button type="button" class="fas-btn fas-btn--ghost" [disabled]="addingItem" (click)="cancelAdd()">{{ 'CANCEL' | translate }}</button>
                    <button
                      type="submit"
                      class="fas-btn fas-btn--primary"
                      [disabled]="addingItem || !newItemTitle.trim() || (addMode === 'area' && !newSectionName.trim())"
                    >
                      @if (addingItem) {
                        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                      }
                      {{ (addingItem ? 'FLEXI_SAFETY_ROUNDS.SAVING' : 'FLEXI_SAFETY_ROUNDS.CHECK.ADD') | translate }}
                    </button>
                  </div>
                </form>
              }
            }

            @if (!searching && sections.length) {
              <div class="fvr-points__section-foot">
                @if (!readOnly && currentSection; as section) {
                  <button
                    type="button"
                    class="fas-btn fas-btn--ghost"
                    [disabled]="bulkBusy || section.graded === section.items.length"
                    (click)="markRestNotApplicable(section.name)"
                  >
                    {{ 'FLEXI_SAFETY_ROUNDS.CHECK.MARK_REST_NA' | translate }}
                  </button>
                }
                <div class="fvr-points__pager">
                  <button type="button" class="fas-btn fas-btn--ghost" [disabled]="sectionIndex === 0" (click)="moveSection(-1)">
                    <i class="pi pi-angle-left" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.CHECK.PREV_SECTION' | translate }}
                  </button>
                  <button
                    type="button"
                    class="fas-btn fas-btn--ghost"
                    [disabled]="sectionIndex >= sections.length - 1"
                    (click)="moveSection(1)"
                  >
                    {{ 'FLEXI_SAFETY_ROUNDS.CHECK.NEXT_SECTION' | translate }}<i class="pi pi-angle-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Sticky footer: where the round stands and the way on. -->
        <div class="fvr-sticky">
          <button type="button" class="fas-btn fas-btn--ghost" (click)="backToList()">
            {{ (readOnly ? 'FLEXI_SAFETY_ROUNDS.BACK' : 'FLEXI_SAFETY_ROUNDS.CHECK.SAVE_LATER') | translate }}
          </button>
          @if (!readOnly) {
            <span class="fvr-sticky__status" role="status">
              @switch (saveState) {
                @case ('saving') {
                  <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.SAVING' | translate }}
                }
                @case ('saved') {
                  <i class="pi pi-check" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.DRAFT_SAVED' | translate: { time: (savedAt | date: 'HH:mm') } }}
                }
                @case ('failed') {
                  <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.SAVE_FAILED' | translate }}
                }
              }
            </span>
          }
          <span class="fvr-sticky__spacer"></span>
          @if (!readOnly && progress.graded < progress.total) {
            <span class="fvr-sticky__remaining">
              {{ 'FLEXI_SAFETY_ROUNDS.REMAINING' | translate: { count: progress.total - progress.graded } }}
              <button type="button" class="fvr-link" (click)="goToFirstUnanswered()">{{ 'FLEXI_SAFETY_ROUNDS.GO_FIRST_UNANSWERED' | translate }}</button>
            </span>
          }
          <button type="button" class="fas-btn fas-btn--primary" (click)="selectStep('finish')">
            {{ 'FLEXI_SAFETY_ROUNDS.CHECK.TO_FINISH' | translate }}
          </button>
        </div>
      </div>
    }

    <!-- ═══════════ Step 3: finish ═══════════ -->
    @if (step === 'finish') {
      <div class="fvr-finish">
        @if (r.Status === Status.Completed) {
          <div class="fvr-success" role="status">
            <div>
              <strong>{{ 'FLEXI_SAFETY_ROUNDS.FINISH.DONE_TITLE' | translate }}</strong>
              {{ 'FLEXI_SAFETY_ROUNDS.FINISH.DONE_TEXT' | translate: { date: (r.CompletedOn | date: 'dd-MM-yyyy HH:mm'), count: findings.length } }}
            </div>
            <div class="fvr-success__actions">
              <button type="button" class="fas-btn fas-btn--ghost" [disabled]="downloading" (click)="downloadReport()">
                <i class="pi" [ngClass]="downloading ? 'pi-spin pi-spinner' : 'pi-file-pdf'" aria-hidden="true"></i>
                {{ (downloading ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.REPORT' : 'FLEXI_SAFETY_ROUNDS.DOWNLOAD_REPORT') | translate }}
              </button>
              @if (justCompleted) {
                <button type="button" class="fas-btn fas-btn--primary" (click)="backToList()">{{ 'FLEXI_SAFETY_ROUNDS.FINISH.TO_LIST' | translate }}</button>
              }
            </div>
          </div>
        }

        @if (!readOnly && completion.ungraded > 0) {
          <div class="fvr-warning" role="alert">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <div class="fvr-warning__body">
              <span>{{ 'FLEXI_SAFETY_ROUNDS.FINISH.UNGRADED' | translate: { count: completion.ungraded } }}</span>
              <div class="fvr-warning__actions">
                <button type="button" class="fas-btn fas-btn--ghost" (click)="goToFirstUnanswered()">{{ 'FLEXI_SAFETY_ROUNDS.GO_FIRST_UNANSWERED' | translate }}</button>
                <button type="button" class="fas-btn fas-btn--ghost" [disabled]="bulkBusy" (click)="confirmMarkAllNotApplicable()">
                  {{ 'FLEXI_SAFETY_ROUNDS.CHECK.MARK_REST_NA' | translate }}
                </button>
              </div>
            </div>
          </div>
        }

        @if (!readOnly && completion.findingsWithoutDeviation > 0) {
          <div class="fvr-warning" role="alert">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <div class="fvr-warning__body">
              <span>{{ 'FLEXI_SAFETY_ROUNDS.FINISH.WITHOUT_DEVIATION' | translate: { count: completion.findingsWithoutDeviation } }}</span>
              <div class="fvr-warning__actions">
                <button type="button" class="fas-btn fas-btn--ghost" (click)="goToFirstFindingWithoutDeviation()">
                  {{ 'FLEXI_SAFETY_ROUNDS.FINISH.GO_FIRST_WITHOUT_DEVIATION' | translate }}
                </button>
              </div>
            </div>
          </div>
        }

        <div class="fvr-tiles">
          @for (tile of distributionTiles; track tile.grade) {
            <div class="fvr-tile" [ngClass]="'fvr-tile--' + tile.grade">
              <span class="fvr-tile__count">{{ tile.count }}</span>
              <span class="fvr-tile__label">{{ tile.labelKey | translate }}</span>
            </div>
          }
        </div>

        <div class="fvr-finish__grid">
          <div class="fvr-finish__main">
            <section class="fvr-findings">
              <h3 class="fvr-h3">{{ 'FLEXI_SAFETY_ROUNDS.FINISH.FINDINGS' | translate: { count: findings.length } }}</h3>
              @for (finding of findings; track finding.Id) {
                <div class="fvr-finding">
                  <span class="fvr-grade" [ngClass]="'fvr-grade--' + finding.Grade">{{ finding.Grade }}</span>
                  <div class="fvr-finding__text">
                    <strong>{{ finding.Title }}</strong>
                    <span class="fas-muted">
                      {{ finding.SectionName }}
                      @if (finding.Photos.length) {
                        · <i class="pi pi-camera" aria-hidden="true"></i>
                        {{ 'FLEXI_SAFETY_ROUNDS.PHOTOS.COUNT' | translate: { count: finding.Photos.length } }}
                      }
                    </span>
                  </div>
                  <div class="fvr-finding__dev">
                    @if (finding.Deviation; as dev) {
                      <a class="fvr-link" [routerLink]="deviationUrl(dev.Id)">{{ dev.Number }}</a>
                      · {{ importanceKey(dev.Importance) | translate }}
                      · {{ dev.ExecutionUserName || '—' }}
                      · {{ dev.Deadline ? (dev.Deadline | date: 'dd-MM-yyyy') : '—' }}
                    } @else {
                      <span class="fvr-finding__missing">{{ 'FLEXI_SAFETY_ROUNDS.FINISH.NO_DEVIATION_YET' | translate }}</span>
                    }
                  </div>
                </div>
              } @empty {
                <p class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.LIST.NO_FINDINGS' | translate }}</p>
              }
            </section>

            <div class="fvr-field">
              <label for="fvr-summary">{{ 'FLEXI_SAFETY_ROUNDS.FINISH.SUMMARY' | translate }}</label>
              <textarea
                pTextarea
                id="fvr-summary"
                rows="4"
                maxlength="4000"
                [(ngModel)]="summary"
                [readonly]="readOnly"
              ></textarea>
            </div>
          </div>

          @if (!readOnly) {
            <aside class="fvr-confirm">
              <p class="fas-note">
                <i class="pi pi-info-circle" aria-hidden="true"></i>
                <span>{{ 'FLEXI_SAFETY_ROUNDS.FINISH.REPORT_NOTE' | translate }}</span>
              </p>
              <label class="fvr-check">
                <input type="checkbox" [(ngModel)]="confirmed" (ngModelChange)="confirmedChanged()" />
                <span>{{ 'FLEXI_SAFETY_ROUNDS.FINISH.CONFIRM' | translate }}</span>
              </label>
              <button type="button" class="fas-btn fas-btn--primary fvr-confirm__submit" [disabled]="!canSubmitCompletion" (click)="complete()">
                @if (completing) {
                  <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                }
                {{ (completing ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.COMPLETING' : 'FLEXI_SAFETY_ROUNDS.FINISH.SUBMIT') | translate }}
              </button>
              @if (completeBlockedKey; as key) {
                <span class="fvr-hint">{{ key | translate }}</span>
              }
            </aside>
          }
        </div>
      </div>
    }
  }
</div>
</section>

<p-dialog
  [visible]="!!preview"
  (visibleChange)="$event ? null : closePreview()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [style]="{ width: '900px', maxWidth: '95vw' }"
  [header]="preview?.FileName ?? ''"
>
  @if (preview; as photo) {
    <div class="fvr-preview">
      <img [src]="photo.Url" [alt]="photo.FileName" (error)="photoLinkFailed()" />
      <a class="fvr-link" [href]="photo.Url" target="_blank" rel="noopener noreferrer">
        {{ 'FLEXI_SAFETY_ROUNDS.PHOTOS.OPEN_NEW_TAB' | translate }} <i class="pi pi-external-link" aria-hidden="true"></i>
      </a>
    </div>
  }
</p-dialog>

<p-menu #standardTextMenu [popup]="true" [model]="standardTextItems" appendTo="body"></p-menu>
<p-confirmDialog key="fvrRound" [style]="{ width: '480px', maxWidth: '95vw' }"></p-confirmDialog>
